import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { InfoCard } from '~/eds/src/components/Card/subcomponents/InfoCard';
import { ErrorPage } from '~/eds/src/components/ErrorPage/ErrorPage';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { DEFAULT_POLL_INTERVAL_MS } from '~/eds/src/constants/duration';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { DataField } from '~/types/Field';
import { Nullable } from '~/types/Nullable';

import { OptimizeResultTag } from '../../../types/optimize';
import { BoostProgress } from './BoostProgress';
import { BoostResults } from './BoostResults';

interface Props {
  field: Nullable<DataField>;
  onRefetch: () => void;
}

export const BoostModal = ({ field, onRefetch }: Props) => {
  const intl = useIntl();
  const highlightMethod = useFlag(FlagType.CustomAiHighlightMethod);
  const { navigate } = useRouting();
  const dispatch = useDispatch();
  const { toast } = useToast();

  const optimize = useSelector(slices.xRay.selectors.selectOptimize);

  const [pollingInterval, setPollingInterval] = useState<number>(
    DEFAULT_POLL_INTERVAL_MS,
  );

  const {
    data: optimizeResults = null,
    isLoading: isLoadingOptimizeResults,
    error: optimizeResultsError,
  } = api.endpoints.getPromptModelOptimizeResults.useQuery(
    // @ts-expect-error -- this query is skipped if optimize is null
    { modelId: optimize?.modelId, version: optimize?.version, highlightMethod },
    {
      skip: !optimize,
      // TODO figure out stop polling when optimizeResults.state === 'done'
      pollingInterval,
    },
  );

  // TODO remove this once above TODO is resolved
  useEffect(() => {
    if (optimizeResults?.state === 'done') {
      setPollingInterval(0);
    }
  }, [optimizeResults]);

  const [
    createPromptModelVersion,
    { isLoading: isCreatingPromptModelVersion },
  ] = api.endpoints.createPromptModelVersion.useMutation();

  const [
    retryOptimizePromptModel,
    { isLoading: isRetryingOptimizePromptModel },
  ] = api.endpoints.optimizePromptModel.useMutation();

  const [selectedOptimizeTag, setSelectedOptimizeTag] =
    useState<Nullable<OptimizeResultTag>>(null);

  const cancelOptimize = () => {
    navigate(RoutePathType.AutomationHubFields);
    dispatch(slices.xRay.actions.resetXRay());
    dispatch(slices.xRay.actions.setOptimize(null));
  };

  const handleConfirmOptimize = (
    selectedTag: OptimizeResultTag,
    options: { showToast: boolean } = { showToast: true },
  ) => {
    if (selectedTag === 'best_overall_no_user_config_changes') {
      selectedTag = 'best_overall';
    }
    const { showToast } = options;
    const selectedResult = optimizeResults!.results!.find(
      (result) => result.tag === selectedTag,
    );

    if (selectedTag) {
      createPromptModelVersion({
        modelId: optimize!.modelId,
        config: selectedResult!.config,
      })
        .unwrap()
        .then(
          () => {
            // success
            onRefetch();
            dispatch(slices.xRay.actions.setOptimize(null));
            if (showToast) {
              toast({
                status: 'success',
                message: intl.formatMessage({
                  id: 'BoostModal.fieldModelUpdated',
                  defaultMessage: 'Field Model Updated.',
                  description:
                    'Status message shown when the field model is successfully updated',
                }),
              });
            }
          },
          () => {
            // error
            if (showToast) {
              toast({
                status: 'danger',
                message: intl.formatMessage({
                  id: 'BoostModal.failedToApplyOptimizations',
                  defaultMessage:
                    'Failed to apply optimizations… Please try again.',
                  description:
                    'Status message shown when applying optimizations fails',
                }),
              });
            }
          },
        );
    }
  };

  if (!optimize) {
    return null;
  }

  const resolveAction = {
    text: intl.formatMessage({
      id: 'BoostModal.retryBoost',
      defaultMessage: 'Retry Boost',
      description: 'Button text to retry boost operation',
    }),
    variant: 'tertiary' as const,
    isLoading: isRetryingOptimizePromptModel,
    onClick: () => {
      retryOptimizePromptModel({
        modelId: optimize.modelId,
        version: optimize.version,
        highlightMethod,
      });
    },
  };
  const errorContent = (
    <ErrorPage preset="service-unavailable" resolveAction={resolveAction} />
  );

  let content = null;
  let primaryAction;

  if (optimizeResultsError) {
    content = errorContent;
  }

  let results, state, progress;

  if (optimizeResults) {
    results = optimizeResults.results;
    state = optimizeResults.state;
    progress = optimizeResults.progress;

    const progressComplete = progress
      ? Number(progress.completed / progress.total)
      : 0;

    switch (state) {
      case 'initializing':
      case 'running':
        content = (
          <BoostProgress progressComplete={progressComplete} state={state} />
        );
        break;
      case 'done':
        if (results) {
          if (
            results.length === 1 &&
            results[0].tag === 'best_no_user_config_changes' &&
            results[0].testCases.every((testCase) => testCase.isNotChanged)
          ) {
            const { tag } = results[0];
            content = (
              <Layout
                align="center"
                w="25%"
                preset="xl"
                direction="column"
                m="auto"
              >
                <InfoCard
                  icon="status-success"
                  iconStatus="success"
                  action={{
                    onClick: () =>
                      handleConfirmOptimize(tag, { showToast: false }),
                    text: intl.formatMessage({
                      id: 'BoostModal.returnToFieldModel',
                      defaultMessage: 'Return to Field Model',
                      description: 'Button text to return to field model',
                    }),
                    level: 'primary',
                  }}
                  title={intl.formatMessage({
                    id: 'BoostModal.noImprovementsFound',
                    defaultMessage:
                      'Boost did not find any improvements to your model.',
                    description: 'Message when no model improvements are found',
                  })}
                />
              </Layout>
            );
          } else {
            content = (
              <BoostResults
                results={results}
                selectedOptimizeTag={selectedOptimizeTag}
                onSelectOptimizeTag={setSelectedOptimizeTag}
              />
            );
            primaryAction = {
              isLoading:
                isCreatingPromptModelVersion || isLoadingOptimizeResults,
              text: intl.formatMessage({
                id: 'BoostModal.confirmSelection',
                defaultMessage: 'Confirm Selection',
                description: 'Button text to confirm optimization selection',
              }),
              onClick: () => handleConfirmOptimize(selectedOptimizeTag!),
            };
          }
        }
        break;
      case 'error':
        content = errorContent;
        break;
    }
  }

  return (
    <>
      <Modal
        disableFooter={state === 'initializing' || state === 'running'}
        loadingContent={{
          isLoading: isLoadingOptimizeResults,
          message: intl.formatMessage({
            id: 'BoostModal.loadingBoostedModel',
            defaultMessage: 'Loading boosted model…',
            description: 'Loading message for boosted model',
          }),
        }}
        isFullPage
        isVisible
        onHide={cancelOptimize}
        primaryAction={primaryAction}
        title={intl.formatMessage(
          {
            id: 'BoostModal.boostingFieldModel',
            defaultMessage: 'Boosting Field Model: {fieldLabel}',
            description:
              'Title for boost modal with field name. Example: fieldLabel = "Contract Value"',
          },
          { fieldLabel: field?.label ?? '' },
        )}
      >
        {content}
      </Modal>
    </>
  );
};
